import generatedCatalog from '../generated/catalog.json'
import { demoCatalog } from '../data/demo-catalog'
import type { Catalog, CatalogItem, ContentType, SearchDocument, SearchIndex } from '../types'

const builtCatalog = generatedCatalog as Catalog

export const catalog: Catalog = builtCatalog.items.length ? builtCatalog : demoCatalog
export const isPreview = !builtCatalog.items.length

function normalize(value: string) {
  return value.toLowerCase().normalize('NFKD').replace(/[\u0300-\u036f]/g, '').match(/[a-z0-9]+/g) ?? []
}

function makeRuntimeIndex(items: CatalogItem[]): SearchIndex {
  const documents: SearchIndex['documents'] = {}
  const exact: SearchIndex['exact'] = {}
  const prefix: SearchIndex['prefix'] = {}
  const add = (target: SearchIndex['exact'], token: string, id: string, score: number) => {
    const postings = target[token] ?? []
    const current = postings.find((posting) => posting[0] === id)
    if (current) current[1] += score
    else postings.push([id, score])
    target[token] = postings
  }
  items.forEach((item) => {
    documents[item.id] = { id: item.id, type: item.type, slug: item.slug, title: item.title, summary: item.summary, department: item.department, role: item.role, date: item.date, tags: item.tags }
    const fields: Array<[string, number]> = [
      [item.title, 12],
      [item.tags.join(' '), 7],
      [item.department, 6],
      [item.role, 6],
      [item.summary, 3],
      [item.body, 1],
      [item.owner, 2],
      [item.location, 2],
    ]
    fields.forEach(([value, weight]) => {
      const counts = new Map<string, number>()
      normalize(value).forEach((token) => counts.set(token, Math.min((counts.get(token) ?? 0) + 1, 3)))
      counts.forEach((count, token) => {
        add(exact, token, item.id, weight * count)
        for (let size = 2; size < Math.min(token.length, 14); size += 1) add(prefix, token.slice(0, size), item.id, Math.max(1, Math.round(weight * count * 0.7)))
      })
    })
  })
  return { version: 1, documents, exact, prefix }
}

// The generated index is retained for build-time/release auditing, but importing its
// multi-megabyte JSON payload made every visitor download it before seeing the site.
// Build the identical 103-document index lazily on the first search interaction.
let searchIndex: SearchIndex | undefined

function getSearchIndex() {
  searchIndex ??= makeRuntimeIndex(catalog.items)
  return searchIndex
}

export function itemsOfType(type: ContentType) {
  return catalog.items.filter((item) => item.type === type)
}

export function findItem(slug: string) {
  return catalog.items.find((item) => item.slug === slug)
}

export function search(query: string, type?: ContentType | 'all', limit = 50): SearchDocument[] {
  const searchIndex = getSearchIndex()
  const terms = normalize(query)
  const docs = Object.values(searchIndex.documents)
  if (!terms.length) return docs.filter((doc) => !type || type === 'all' || doc.type === type).slice(0, limit)
  const scores = new Map<string, number>()
  terms.forEach((term) => {
    const postings = searchIndex.exact[term] ?? searchIndex.prefix[term] ?? []
    postings.forEach(([id, score]) => scores.set(id, (scores.get(id) ?? 0) + score))
  })
  return [...scores.entries()]
    .map(([id, score]) => ({ doc: searchIndex.documents[id], score }))
    .filter(({ doc }) => doc && (!type || type === 'all' || doc.type === type))
    .sort((a, b) => b.score - a.score || b.doc.date.localeCompare(a.doc.date))
    .slice(0, limit)
    .map(({ doc }) => doc)
}
